import { GenericMessage } from "components/feedback/Message";
import { NsKey } from "enums/NsKey";
import { formatSubsidiaryAmount, formatSubsidiaryCount } from "features/new-actor/components/subsidiaries/subsidiaryFormatters";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { CircleCheckBig, CircleX, Info, TriangleAlert } from "lucide-react";
import { FC, ReactNode } from "react";
import { EligibilityFigureKey, EligibilityVariant, EligibilityView } from "./getEligibilityView";
import { LegalProtectionStatus, LP_STATUS } from "./legalProtectionTheme";

type VerdictVariant = Exclude<EligibilityVariant, "notApplicable">;

const STATUS_LABELS: Record<VerdictVariant, string> = {
  eligible: "SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.ELIGIBILITY.STATUS.ELIGIBLE",
  toComplete: "SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.ELIGIBILITY.STATUS.TO_COMPLETE",
  nonEligible: "SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.ELIGIBILITY.STATUS.NON_ELIGIBLE",
};

const STATUS_ICONS: Record<LegalProtectionStatus, ReactNode> = {
  eligible: <CircleCheckBig className="h-7 w-7" />,
  toComplete: <TriangleAlert className="h-7 w-7" />,
  nonEligible: <CircleX className="h-7 w-7" />,
  neutral: <Info className="h-7 w-7" />,
};

const figureOf = (view: EligibilityView | null, key: EligibilityFigureKey): number | undefined =>
  view?.figures.find(figure => figure.key === key)?.value;

interface LegalProtectionEligibilityBannerProps {
  /** Verdict of the back; null while nothing is loaded. */
  view: EligibilityView | null;
  /** The verdict could not be read. */
  hasError?: boolean;
  onRetry?: () => void;
}

/**
 * Verdict of the legal protection eligibility at the bottom of the first step, kept minimal: one
 * block of text (status, cumulated revenue against the ceiling, what is left or exceeded) and the
 * reasons if any. No chart. The
 * headcount and the number of subsidiaries are already shown with the accounting data. Status, reasons and figures are the back's, shown as they are — even the
 * ceiling of the gauge, which is never written here.
 */
export const LegalProtectionEligibilityBanner: FC<LegalProtectionEligibilityBannerProps> = ({ view, hasError, onRetry }) => {
  const { t } = useDynamicTranslation([NsKey.Common]);

  // The verdict of the back drives the colours of the banner; no verdict yet keeps it neutral.
  const status: LegalProtectionStatus = view && view.variant !== "notApplicable" ? view.variant : "neutral";
  const theme = LP_STATUS[status];

  // The ceiling is the back's: without it, or without a cumulated revenue, there is nothing to compare.
  const totalRevenue = figureOf(view, "totalRevenue");
  const revenueCeiling = figureOf(view, "revenueCeiling");
  const hasFigures = totalRevenue != null && revenueCeiling != null && revenueCeiling > 0;
  const overflow = hasFigures ? Math.max(totalRevenue - revenueCeiling, 0) : 0;

  return (
    <div className="flex flex-col gap-4">
      {hasError && (
        <GenericMessage
          type="error"
          message={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.ELIGIBILITY.LOAD_ERROR")}
          dismissible={false}
          actionButton={onRetry ? {
            label: t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.RETRY"),
            onClick: onRetry,
          } : undefined}
        />
      )}

      {/* The rule does not apply to this folder: no verdict at all, as before. */}
      {view?.variant !== "notApplicable" && (
        <section className={`flex flex-col gap-3 rounded-xl border px-6 py-4 ${theme.background} ${theme.border}`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className={`flex flex-none ${theme.softText}`}>
                {STATUS_ICONS[status]}
              </span>
              <div className="flex flex-col">
                <p className={`text-[16px] font-bold leading-tight ${theme.text}`}>
                  {view
                    ? t(STATUS_LABELS[view.variant])
                    : t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.ELIGIBILITY.STATUS.UNKNOWN")}
                </p>
                {/* What is left under the ceiling, or what exceeds it. */}
                {hasFigures && (
                  <p className={`text-[13px] tabular-nums ${theme.softText}`}>
                    {overflow > 0
                      ? t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.ELIGIBILITY.OVERRUN", {
                        amount: formatSubsidiaryAmount(overflow)
                      })
                      : t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.ELIGIBILITY.REMAINING", {
                        amount: formatSubsidiaryAmount(revenueCeiling - totalRevenue)
                      })}
                  </p>
                )}
              </div>
            </div>
            {/* Cumulated figures of the company and its subsidiaries, all the back's. */}
            {hasFigures && (
              <div className="flex flex-col items-end gap-0.5">
                <p className={`flex items-baseline gap-1.5 tabular-nums ${theme.softText}`}>
                  <span className={`text-[20px] font-bold ${theme.text}`}>{formatSubsidiaryAmount(totalRevenue)}</span>
                  <span className="text-[13px]">/ {formatSubsidiaryAmount(revenueCeiling)}</span>
                </p>
                <p className={`text-[13px] tabular-nums ${theme.softText}`}>
                  {t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.ELIGIBILITY.HEADCOUNT", {
                    employees: formatSubsidiaryCount(figureOf(view, "totalEmployees")),
                    subsidiaries: formatSubsidiaryCount(figureOf(view, "subsidiariesCount"))
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Reasons of the verdict: the back's wording, never rewritten here. */}
          {view
            ? view.reasons.map(reason => (
              <p key={reason} className={`text-[13px] ${theme.softText}`}>{reason}</p>
            ))
            : (
              <p className={`text-[13px] ${theme.softText}`}>
                {t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.ELIGIBILITY.UNKNOWN_HINT")}
              </p>
            )}
        </section>
      )}
    </div>
  );
};
